/**
 * 運営者コンソール専用の HTTP クライアント。
 *
 * 面は **ops 固定**。`/api/v1/portal` を叩く経路をここに作らない（面の取り違えは
 * JWT の `aud` で弾かれるが、そもそも呼べないようにしておくのが早い）。
 *
 * 設計上の決め事:
 *  - アクセストークンは **メモリのみ**に置く。05-ui は「メモリ + localStorage」と
 *    書いているが、refresh は path 分離の httpOnly Cookie（`/api/v1/auth/ops`）で
 *    渡ってくるので、リロード後は `/refresh` を 1 回叩けば復帰できる。
 *    運営者トークンを localStorage に置くと XSS 一発で 12 時間分の全権が漏れるため、
 *    永続化しない方を採る。
 *  - refresh はローテーション + 再利用検知（§7.1）。同時に 2 本走らせると
 *    family ごと失効させられるので、タブ内は in-flight 共有、タブ間は
 *    `navigator.locks` で直列化する。
 *  - エラーは `{error:{code,message,details}}` 封筒（§4.3）。GET の失敗は各画面が
 *    Alert で出すので黙らせ、書き込み系の失敗だけ toast する（下記 shouldNotify）。
 */
import { parseErrorEnvelope } from '@mail-sv/shared';

/* ------------------------------------------------------------- ベース URL */

/** 別オリジン運用時のみ設定。空なら同一オリジン（dev は Vite proxy）。 */
const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export const OPS_PREFIX = '/api/v1/ops';
export const AUTH_PREFIX = '/api/v1/auth/ops';
export const PUBLIC_PREFIX = '/api/v1/public';

/* --------------------------------------------------------------- エラー */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** 権限不足。画面側で文言を差し替えるために判定を一箇所に置く。 */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** 再認証が必要（fresh-auth 対象操作）。 */
  get needsReauth(): boolean {
    return this.code === 'REAUTH_REQUIRED';
  }
}

/** ネットワーク断など、封筒すら受け取れなかったケース。 */
export class NetworkError extends ApiError {
  constructor(cause: unknown) {
    super(0, 'NETWORK_ERROR', 'サーバーに接続できませんでした。通信環境を確認してください。', cause);
    this.name = 'NetworkError';
  }
}

/* ------------------------------------------------- 外部へ差し込む口（副作用） */

type Notifier = (error: ApiError) => void;
type UnauthorizedHandler = () => void;
/** 再認証 UI。成功したら true を返し、呼び出し元が元の操作を 1 回だけ再試行する。 */
type ReauthHandler = () => Promise<boolean>;

let notifier: Notifier | null = null;
let onUnauthorized: UnauthorizedHandler | null = null;
let reauthHandler: ReauthHandler | null = null;

/** toast 実装を注入する。React 側（ToastProvider の内側）から 1 回呼ぶ。 */
export function setApiErrorNotifier(fn: Notifier | null): void {
  notifier = fn;
}

/** セッション切れの後始末（ストア破棄 + `/login` へ）。 */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

/**
 * `REAUTH_REQUIRED`（fresh-auth 切れ）を受けたときに開く再認証 UI を差し込む。
 * React 側（`<ReauthGate>`）から 1 回登録する。
 */
export function setReauthHandler(fn: ReauthHandler | null): void {
  reauthHandler = fn;
}

/* --------------------------------------------------------- トークン保持 */

let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/* ----------------------------------------------------------- クエリ組立 */

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

/** null / undefined / 空文字を落として `?a=1&b=2` を作る。 */
function toSearch(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.length === 0) continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function buildUrl(path: string, params?: QueryParams): string {
  return `${API_ORIGIN}${path}${toSearch(params)}`;
}

/* ---------------------------------------------------------------- refresh */

let inflightRefresh: Promise<string | null> | null = null;

async function withCrossTabLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  // Safari 15 以前など locks が無い環境では直列化を諦める（タブ 1 枚なら問題ない）。
  const locks = navigator.locks;
  if (!locks) return fn();
  return locks.request(name, fn);
}

async function requestRefresh(): Promise<string | null> {
  const res = await fetch(buildUrl(`${AUTH_PREFIX}/refresh`), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  const body = (await res.json()) as { accessToken?: unknown };
  return typeof body.accessToken === 'string' ? body.accessToken : null;
}

/**
 * アクセストークンを取り直す。失敗したら null（呼び出し側がログアウト扱いにする）。
 * 同時多発しても実際に飛ぶリクエストは 1 本になる。
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;

  const tokenAtStart = accessToken;
  inflightRefresh = withCrossTabLock('mail-sv.ops.refresh', async () => {
    // 別タブがロック中に refresh を終えていれば、その結果に乗る。
    // ここで二重に叩くと再利用検知に引っかかって family ごと失効する。
    if (accessToken !== null && accessToken !== tokenAtStart) return accessToken;
    const token = await requestRefresh().catch(() => null);
    accessToken = token;
    return token;
  }).finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
}

/* ------------------------------------------------------------ リクエスト */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
  /** 作成系 POST の再送を安全にする。同じ mutation のリトライでは同じ値を送る。 */
  idempotencyKey?: string;
  /** Authorization を付けない（ログイン・公開設定）。 */
  anonymous?: boolean;
  /** 401 での自動 refresh を無効化（refresh 自身・ログイン）。 */
  noRefresh?: boolean;
  /** `REAUTH_REQUIRED` を受けても再認証ダイアログを出さない（再認証 API 自身）。 */
  noReauth?: boolean;
  /** true で toast を抑止、false で強制。既定は「GET 以外だけ toast」。 */
  silent?: boolean;
  /** レスポンスを JSON ではなく Blob で受け取る（CSV / PDF ダウンロード）。 */
  raw?: boolean;
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* 本文が JSON でないケース（502 の HTML など）はそのまま握る */
  }
  const envelope = parseErrorEnvelope(body);
  if (envelope) {
    return new ApiError(res.status, envelope.code, envelope.message, envelope.details);
  }
  return new ApiError(res.status, 'INTERNAL_ERROR', `サーバーエラーが発生しました (HTTP ${res.status})`);
}

async function rawRequest(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!opts.anonymous && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    // refresh Cookie を載せる。オリジンが分かれていても API 側で CORS 許可済み。
    credentials: 'include',
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  return fetch(buildUrl(path, opts.query), init);
}

function shouldNotify(opts: RequestOptions): boolean {
  if (opts.silent !== undefined) return !opts.silent;
  // GET は画面が Alert / EmptyState で見せる。二重に出さない。
  return (opts.method ?? 'GET') !== 'GET';
}

/** 送信 1 回ぶん。ネットワーク断だけを `NetworkError` に均す。 */
async function send(path: string, opts: RequestOptions): Promise<Response> {
  try {
    return await rawRequest(path, opts);
  } catch (cause) {
    const err = new NetworkError(cause);
    if (shouldNotify(opts)) notifier?.(err);
    throw err;
  }
}

/** 2xx を本文に、それ以外を `ApiError` に変換する（401 の分岐を通り抜けた後）。 */
async function finish<T>(res: Response, opts: RequestOptions): Promise<T> {
  if (!res.ok) {
    const err = await toApiError(res);
    if (shouldNotify(opts)) notifier?.(err);
    throw err;
  }

  if (opts.raw) return (await res.blob()) as unknown as T;
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts);

  if (res.status === 401 && !opts.anonymous) {
    // **401 の中身を見てから分岐する。**
    // `REAUTH_REQUIRED`（fresh-auth 切れ）は「セッションが死んだ」ではないのに、
    // 以前はここで一括して refresh → 再 401 → セッション破棄まで進んでいた。
    // refresh しても `aat`（2 要素目を通した時刻）は進まないので必ず再 401 になり、
    // **設定変更やスタッフ管理を押した運営者がその場でログアウトさせられていた**。
    const first = await toApiError(res);

    if (first.needsReauth) {
      if (!opts.noReauth && reauthHandler) {
        const ok = await reauthHandler().catch(() => false);
        if (ok) {
          // 再認証はセッションを差し替える（アクセストークンも入れ替わっている）。
          // 元の操作をそのまま 1 回だけ流し直す。
          res = await send(path, opts);
          return finish<T>(res, opts);
        }
      }
      if (shouldNotify(opts)) notifier?.(first);
      throw first;
    }

    /*
     * **`MFA_REQUIRED` でセッションを捨てない。**
     *
     * ops 面は TOTP 必須（`plugins/ops-guard.ts`）で、未登録の staff は
     * すべての ops ルートで `MFA_REQUIRED` を受ける。そのステータスは
     * **401**（`packages/shared/src/errors.ts`）なので、下の「通常の 401」に
     * 落ちると refresh → 再 401 →**ログアウト**まで一直線に進む。
     *
     * 結果、TOTP 未登録の運営者は
     *   ログイン成功 → ダッシュボードが一瞬描画 → 追い出される
     * を繰り返し、**登録を促す `MfaEnrollmentGate` に一度も到達できない**
     * （＝初回ログインが原理的に完了しない）。2026-08-30 に AWS で踏んだ。
     *
     * セッションは生きている（`/auth/ops/me` は 200 を返す）ので、
     * ここは投げるだけにして画面にガードを出させる。**上の
     * `REAUTH_REQUIRED` と同じ理由の、同じ形の修正**である。
     */
    if (first.code === 'MFA_REQUIRED') {
      if (shouldNotify(opts)) notifier?.(first);
      throw first;
    }

    // 通常の 401 は 1 回だけ silent refresh してから再試行する。
    if (!opts.noRefresh) {
      const token = await refreshAccessToken();
      if (token) {
        res = await send(path, opts);
        if (res.status !== 401) return finish<T>(res, opts);
      }
    }

    accessToken = null;
    onUnauthorized?.();
    // ここで toast は出さない。ログイン画面へ飛ぶこと自体が説明になっている。
    throw new ApiError(401, 'UNAUTHORIZED', 'セッションの有効期限が切れました。再度ログインしてください。');
  }

  return finish<T>(res, opts);
}

/* ------------------------------------------------------- ops 面ショートカット */

/** `/api/v1/ops` 配下を叩く。path は `/nodes` のようにスラッシュ始まりで渡す。 */
export const api = {
  get: <T>(path: string, query?: QueryParams, opts?: RequestOptions): Promise<T> =>
    request<T>(`${OPS_PREFIX}${path}`, { ...opts, method: 'GET', query }),

  post: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>(`${OPS_PREFIX}${path}`, { ...opts, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>(`${OPS_PREFIX}${path}`, { ...opts, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>(`${OPS_PREFIX}${path}`, { ...opts, method: 'PUT', body }),

  del: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>(`${OPS_PREFIX}${path}`, { ...opts, method: 'DELETE' }),

  /** 認証面（`/api/v1/auth/ops`）。 */
  auth: <T>(path: string, opts: RequestOptions = {}): Promise<T> =>
    request<T>(`${AUTH_PREFIX}${path}`, opts),

  /** 公開面（`/api/v1/public`）。 */
  public: <T>(path: string, opts: RequestOptions = {}): Promise<T> =>
    request<T>(`${PUBLIC_PREFIX}${path}`, { ...opts, anonymous: true, noRefresh: true }),
};

/* ------------------------------------------------------ ページング補助 */

/**
 * cursor 一覧のクエリを組む。`limit` は API 側の上限が 200（§4.3）。
 * フィルタは呼び出し側の search params をそのまま渡してよい（空値は落ちる）。
 */
export function cursorQuery(
  limit: number,
  cursor: string | null,
  filters?: QueryParams,
): QueryParams {
  return { ...filters, limit: Math.min(Math.max(limit, 1), 200), cursor: cursor ?? undefined };
}

/** 作成系 mutation ごとに 1 回だけ生成し、リトライでも同じ値を使う。 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * presigned URL を返す GET（請求書 PDF）を開く。
 *
 * **Blob で受け取らない。** ops 面の `GET /invoices/:id/pdf` は本体ではなく
 * 署名済み URL（`{url, expiresAt, ...}`）を返す。`download()`（Blob 保存）で
 * 叩くと JSON がそのまま `.pdf` として保存され、開けないファイルになる
 * （以前は POST しか無かったため 404 で、そもそも 1 通も取り出せなかった）。
 *
 * 取得は非同期なので、**クリックの瞬間に空タブを開いてから**遷移させる。
 * `await` の後で `window.open` するとポップアップブロックに掛かる。
 */
export async function openPresigned(path: string, query?: QueryParams): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;
  try {
    const res = await request<{ url: string }>(`${OPS_PREFIX}${path}`, {
      method: 'GET',
      query,
      // GET は既定で黙るが、この操作は「開かない」以外の手掛かりが無いので必ず出す。
      silent: false,
    });
    if (tab) tab.location.href = res.url;
    else window.location.href = res.url;
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/** CSV / NDJSON などのダウンロード。ブラウザに保存させるところまで面倒を見る。 */
export async function download(path: string, filename: string, query?: QueryParams): Promise<void> {
  const blob = await request<Blob>(`${OPS_PREFIX}${path}`, { method: 'GET', query, raw: true, silent: false });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
